import React from "react";
import { SectionHeading } from "./SectionHeading";
import { TrendingUp, Calendar, DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";
import { Reveal } from "./Reveal";

const columns = [
  {
    staff: "Amara",
    role: "Senior Stylist",
    appts: [
      { time: "9:30", client: "Sophie R.", svc: "Balayage", tone: "brand" },
      { time: "12:00", client: "Mia T.", svc: "Cut & Style", tone: "cyan" },
      { time: "2:30", client: "Olivia P.", svc: "Gloss", tone: "muted" },
    ],
  },
  {
    staff: "Jordan",
    role: "Barber",
    appts: [
      { time: "10:00", client: "Liam K.", svc: "Beard Sculpt", tone: "muted" },
      { time: "11:15", client: "Noah W.", svc: "Skin Fade", tone: "brand" },
      { time: "3:00", client: "Ethan M.", svc: "Hot Towel", tone: "cyan" },
    ],
  },
  {
    staff: "Priya",
    role: "Makeup Artist",
    appts: [
      { time: "9:00", client: "Ava L.", svc: "Bridal Trial", tone: "pink" },
      { time: "1:00", client: "Grace H.", svc: "Glam", tone: "muted" },
    ],
  },
];

const stats = [
  {
    label: "Revenue today",
    value: "$2,840",
    delta: "+12%",
    icon: DollarSign,
  },
  { label: "Bookings", value: "24", delta: "+8%", icon: Calendar },
  { label: "Rebook rate", value: "74%", delta: "+5%", icon: TrendingUp },
];

const toneMap = {
  brand: "border-brand/40 bg-brand/10",
  cyan: "border-cyan/40 bg-cyan/10",
  pink: "border-pink/40 bg-pink/10",
  muted: "border-border bg-secondary",
};

export default function Dashboard() {
  return (
    <section className="px-4 py-12 sm:py-16">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <SectionHeading
            eyebrow="Salon management"
            title="Your whole day, beautifully organised"
            description="Appointments, clients, staff and revenue — all in one place, always up to date."
          />
        </Reveal>

        <Reveal delay={0.1} y={32}>
          <div className="relative mt-12">
            <div
              aria-hidden="true"
              className="absolute inset-x-10 -top-6 bottom-10 -z-10 rounded-[2rem] bg-brand/10 blur-3xl"
            />
            <div className="glass overflow-hidden rounded-3xl p-2">
              <div className="rounded-[1.6rem] border border-border bg-background">
                {/* top bar */}
                <div className="flex flex-col gap-4 border-b border-border p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Today</p>
                    <p className="font-heading text-2xl font-bold text-foreground">
                      Tuesday, 14 May
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 sm:gap-3">
                    {stats.map((s) => (
                      <div
                        key={s.label}
                        className="rounded-xl border border-border bg-card px-2.5 py-2 sm:px-4 sm:py-3"
                      >
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <s.icon className="size-3.5 shrink-0" />
                          <span className="hidden text-[11px] sm:inline">
                            {s.label}
                          </span>
                        </div>
                        <p className="mt-1 text-sm font-semibold text-foreground sm:text-lg truncate">
                          {s.value}
                        </p>
                        <p className="text-[11px] font-semibold text-brand">
                          {s.delta}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* calendar columns — 2 on mobile (hide Priya), 3 sm+ */}
                <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-3">
                  {columns.map((col, ci) => (
                    <div
                      key={col.staff}
                      className={cn(
                        "bg-background p-4",
                        ci === 2 && "hidden sm:block",
                      )}
                    >
                      <div className="mb-4 flex items-center gap-2.5">
                        <div className="flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-brand to-cyan text-xs font-semibold text-white">
                          {col.staff[0]}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {col.staff}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {col.role}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2.5">
                        {col.appts.map((a) => (
                          <div
                            key={a.time + a.client}
                            className={cn(
                              "rounded-xl border p-3",
                              toneMap[a.tone],
                            )}
                          >
                            <p className="text-[11px] text-muted-foreground">
                              {a.time}
                            </p>
                            <p className="text-sm font-medium text-foreground">
                              {a.client}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {a.svc}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
